import React from 'react';
import { Label } from '@/components/shared/label';
import { Slider } from '@/components/shared/slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shared/tooltip';
import { Info } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/shared/radio-group';
import { useIsMobile } from '@/hooks/use-mobile';

interface ValueSliderProps {
  label: string;
  tooltip: string;
  value: number;
  onChange: (value: number[]) => void;
  isChallengeMetric?: boolean;
}

const ValueSlider = ({ label, tooltip, value, onChange, isChallengeMetric = false }: ValueSliderProps) => {
  const isMobile = useIsMobile();

  const getPriorityLabel = (value: number) => {
    if (isChallengeMetric) {
      switch (value) {
        case 1:
          return 'Very Low Concern';
        case 2:
          return 'Low Concern';
        case 3:
          return 'Moderate Concern';
        case 4:
          return 'High Concern';
        case 5:
          return 'Very High Concern';
        default:
          return 'Moderate Concern';
      }
    }
    
    switch (value) {
      case 1:
        return 'Very Low';
      case 2:
        return 'Low';
      case 3:
        return 'Moderate';
      case 4:
        return 'High';
      case 5:
        return 'Critical';
      default:
        return 'Moderate';
    }
  };

  const handleRadioChange = (value: string) => {
    onChange([parseInt(value)]);
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Label className="text-base md:text-lg font-medium">{label}</Label>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="focus:outline-none">
                  <Info className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              </TooltipTrigger>
              <TooltipContent 
                side="right" 
                sideOffset={5}
                className="bg-gold text-black border-none"
              >
                <p className="max-w-xs text-base">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <span className="text-base md:text-lg font-medium text-primary">
          {getPriorityLabel(value)}
        </span>
      </div>

      {isMobile ? (
        <RadioGroup
          value={value.toString()}
          onValueChange={handleRadioChange}
          className="flex flex-col space-y-2"
        >
          {[1, 2, 3, 4, 5].map((val) => (
            <div key={val} className="flex items-center space-x-2">
              <RadioGroupItem value={val.toString()} id={`${label}-${val}`} />
              <Label htmlFor={`${label}-${val}`} className="text-sm">
                {getPriorityLabel(val)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      ) : (
        <div className="flex items-center gap-4">
          <span className="text-sm md:text-base text-gray-500 min-w-[80px]">
            {isChallengeMetric ? "Low Concern" : "Low Priority"}
          </span>
          <Slider
            value={[value]}
            min={1}
            max={5}
            step={1}
            onValueChange={onChange}
            className="flex-1"
          />
          <span className="text-sm md:text-base text-gray-500 min-w-[80px]">
            {isChallengeMetric ? "High Concern" : "High Priority"}
          </span>
        </div>
      )}
    </div>
  );
};

export default ValueSlider;